import type * as MT from 'src/motornet/types';
import type { VehicleRef } from '../domain';
import {
	SAVED_CONFIG_SCHEMA_VERSION,
	type ConfigurationDraft,
	type SavedCarConfiguration,
	type SavedConfigurationPayload,
	type SavedConfigurationRow,
	type SelectedOffer,
	type StockPayload,
	type UnavailableReason,
} from './types';

const REASONS: Record<
	MT.PlatformConfigurationUnavailableReason,
	UnavailableReason
> = {
	excluded: 'excluded',
	'not-quotable': 'not_quotable',
};

const refFrom = (
	metadata: MT.PlatformConfigurationMetadata
): VehicleRef | null =>
	metadata.vehicleCode && metadata.vehicleKind && metadata.year != null
		? {
				eurotaxCode: metadata.vehicleCode,
				vehicleKind: metadata.vehicleKind as MT.MotornetVehicleKind,
				year: metadata.year,
				month: metadata.month ?? 1,
		  }
		: null;

export function toApiBody(
	draft: ConfigurationDraft,
	code: string,
	offer: SelectedOffer
): MT.SaveConfigurationBody {
	const { listing, pricesAtSave, vehicleRef } = draft;

	return {
		version: SAVED_CONFIG_SCHEMA_VERSION,
		metadata: {
			code,
			makeCode: listing.makeCode,
			makeLabel: listing.makeLabel,
			modelCode: listing.modelCode,
			modelLabel: listing.modelLabel,
			trimLabel: listing.trimLabel,
			vehicleCode: listing.trimCode,
			vehicleKind: vehicleRef?.vehicleKind ?? null,
			year: vehicleRef?.year ?? null,
			month: vehicleRef?.month ?? null,
			stockVehicleId: draft.vehicleStockId,
			listPrice: pricesAtSave.listPriceNoVat,
			optionalsTotal: pricesAtSave.optionalsNoVat,
			totalPrice: pricesAtSave.totalNoVat,
			imageUrl: draft.imageUrl,
		},
		payload: {
			...draft.payload,
			offer,
			parentId: draft.parentId,
		},
	};
}

export function fromApiListItem(
	item: MT.PlatformConfigurationListItem
): SavedConfigurationRow {
	const { metadata } = item;

	return {
		id: item.id,
		version: item.version,
		code: metadata.code,
		createdAt: item.createdAt,
		updatedAt: item.updatedAt,
		available: item.available,
		unavailableReason: item.unavailableReason
			? REASONS[item.unavailableReason]
			: null,
		vehicleType: metadata.stockVehicleId ? 'stock' : 'new',
		vehicleStockId: metadata.stockVehicleId ?? null,
		vehicleRef: refFrom(metadata),
		imageUrl: metadata.imageUrl ?? null,
		listing: {
			makeCode: metadata.makeCode,
			makeLabel: metadata.makeLabel,
			modelCode: metadata.modelCode,
			modelLabel: metadata.modelLabel,
			trimCode: metadata.vehicleCode ?? null,
			trimLabel: metadata.trimLabel ?? null,
		},
		pricesAtSave: {
			// `metadata` non ha valuta: la lista è a mercato singolo.
			currency: 'EUR',
			listPriceNoVat: metadata.listPrice ?? 0,
			optionalsNoVat: metadata.optionalsTotal ?? 0,
			totalNoVat: metadata.totalPrice ?? 0,
		},
	};
}

export function fromApiRecord(
	record: MT.PlatformConfiguration
): SavedCarConfiguration {
	const row = fromApiListItem(record);
	const payload = record.payload as unknown;

	const currency =
		(payload as { currency?: unknown })?.currency ?? row.pricesAtSave.currency;

	const withCurrency: SavedConfigurationRow = {
		...row,
		pricesAtSave: {
			...row.pricesAtSave,
			currency: typeof currency === 'string' ? currency : 'EUR',
		},
	};

	return row.vehicleType === 'stock'
		? {
				...withCurrency,
				vehicleType: 'stock',
				vehicleStockId: row.vehicleStockId as string,
				payload: payload as StockPayload,
		  }
		: {
				...withCurrency,
				vehicleType: 'new',
				vehicleStockId: null,
				payload: payload as SavedConfigurationPayload,
		  };
}
